import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider } from './components/AuthProvider';
import { router } from './router';
import { flush, pendingCount } from './offline/queue';
import { flushCanjes, pendingCanjeCount } from './offline/canjeQueue';
import { flushSoftys, pendingSoftysCount } from './offline/softysQueue';
import { iniciarVigilanciaDeVersion } from './lib/actualizacionDeVersion';
import './api/attendance'; // efecto: registra el enviador real de la cola offline
import './api/canjes'; // efecto: registra el enviador real de la cola de canjes
import './api/softys'; // efecto: registra el enviador real de la cola de visitas a puestos

/** Backoff exponencial acotado de la cola (spec §9: "reintentos exponenciales").
 *  5 s → 10 s → 20 s → … → tope 5 min. */
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;

/** Sube la cola local al abrir la app, cada vez que vuelve la conexión y —mientras
 *  queden pendientes— con reintentos exponenciales. Sin esto, un fallo transitorio del
 *  servidor (5xx, timeout de Storage) con el dispositivo online dejaría el marcado en
 *  IndexedDB hasta que el recurso cerrara y reabriera la PWA, cosa que en campo no pasa:
 *  la app queda abierta toda la jornada. Avisa a las pantallas con el evento
 *  'queue-flushed'. */
function useQueueSync(): void {
  useEffect(() => {
    let cancelled = false;
    let running = false;
    let delay = RETRY_MIN_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const run = async () => {
      if (cancelled || running) return;
      running = true;
      try {
        const result = await flush();
        window.dispatchEvent(new CustomEvent('queue-flushed', { detail: result }));
        // Hubo progreso real: el próximo reintento vuelve a ser inmediato-corto.
        if (result.sent > 0) delay = RETRY_MIN_MS;
      } catch {
        // Sin red o IndexedDB no disponible: se reintenta con el backoff de abajo.
      }
      // Los CANJES van en su propio try: hasta ahora solo subían al abrir la pantalla de
      // canjes, así que un sábado en Makro —registrar, cerrar turno y guardar el celular— los
      // dejaba en el teléfono hasta la próxima vez que alguien entrara a esa pantalla, o para
      // siempre. Aparte del de asistencia a propósito: que falle la cola de canjes no puede
      // impedir que suba un marcado, que es de lo que depende el pago de la jornada.
      try {
        const r = await flushCanjes();
        window.dispatchEvent(new CustomEvent('canjes-flushed', { detail: r }));
        if (r.sent > 0) delay = RETRY_MIN_MS;
      } catch {
        // Igual que arriba: lo reintenta el backoff.
      }
      // 🔴 Y las VISITAS A PUESTOS de Softys, por la MISMA razón que los canjes y con el mismo
      // defecto encontrado el 14-set a la madrugada: `flushSoftys` solo se llamaba desde la
      // pantalla de registro. Una impulsadora que registra treinta puestos sin señal en un
      // mercado, marca su salida y guarda el telefono, se llevaba **los treinta a la casa**: no
      // había nada que los reintentara hasta que alguien volviera a abrir esa pantalla exacta
      // con señal. El `import './api/softys'` de arriba es la otra mitad del arreglo — sin el,
      // la cola no tiene enviador registrado y esto tira una excepción en silencio.
      try {
        const s = await flushSoftys();
        window.dispatchEvent(new CustomEvent('softys-flushed', { detail: s }));
        if (s.sent > 0) delay = RETRY_MIN_MS;
      } catch {
        // Igual que arriba: lo reintenta el backoff.
      } finally {
        running = false;
      }
      if (cancelled) return;

      // El backoff sigue vivo mientras quede algo de CUALQUIERA de las dos colas: mirando solo
      // la de asistencia, un canje pendiente con la asistencia ya enviada no se reintentaría
      // nunca más.
      let left = 0;
      try {
        left = await pendingCount();
      } catch {
        left = 0;
      }
      try {
        left += await pendingCanjeCount();
      } catch {
        // Si no se puede contar, se decide con lo que se pudo leer.
      }
      try {
        left += await pendingSoftysCount();
      } catch {
        // Igual que arriba.
      }
      if (cancelled || left === 0) return;

      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        void run();
      }, delay);
      delay = Math.min(delay * 2, RETRY_MAX_MS);
    };

    // Volvió la conexión: reintento inmediato y backoff reseteado.
    const onOnline = () => {
      delay = RETRY_MIN_MS;
      clearTimer();
      void run();
    };

    void run();
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      clearTimer();
      window.removeEventListener('online', onOnline);
    };
  }, []);
}

/** Trae sola la versión nueva al celular que ya está en el mercado. El porqué de cada decisión
 *  —sobre todo por qué NO recarga apenas se entera— esta en `lib/actualizacionDeVersion.ts`. */
function useVigilanciaDeVersion(): void {
  useEffect(() => {
    const v = iniciarVigilanciaDeVersion();
    return () => v.detener();
  }, []);
}

export default function App() {
  useQueueSync();
  useVigilanciaDeVersion();
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
